/**
 * Serviço para validação do reCAPTCHA no backend
 * 
 * NOTA: Este serviço é um exemplo de como validar o token do reCAPTCHA.
 * Em produção, você deve implementar esta validação no seu backend (Firebase Functions, API, etc.)
 */

interface RecaptchaVerificationResponse {
  success: boolean;
  score: number;
  action: string;
  challenge_ts?: string;
  hostname?: string;
  'error-codes'?: string[];
}

/**
 * Valida o token do reCAPTCHA
 * 
 * ⚠️ IMPORTANTE: A validação completa deve ser feita no BACKEND (Firebase Functions, API, etc.)
 * A Secret Key NUNCA deve ser exposta no frontend.
 * 
 * Esta função faz uma validação básica. Para produção, implemente a validação no backend.
 * 
 * @param token - Token gerado pelo reCAPTCHA v3
 * @returns Promise com o resultado da validação
 */
export const validateRecaptchaToken = async (
  token: string
): Promise<{ success: boolean; score: number; error?: string }> => {
  // ⚠️ ATENÇÃO: Validação no frontend é apenas básica
  // Para produção, você DEVE validar no backend usando a Secret Key
  
  const secretKey = process.env.REACT_APP_RECAPTCHA_SECRET_KEY;

  // Se não tiver Secret Key no frontend (recomendado), apenas valida se o token existe
  if (!secretKey) {
    // Em desenvolvimento, aceita se o token existe
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️ RECAPTCHA_SECRET_KEY não configurada no frontend. Validação básica aplicada.');
      return { success: !!token, score: token ? 0.8 : 0 };
    }
    // Em produção sem Secret Key, requer validação no backend
    console.warn('⚠️ Validação do reCAPTCHA deve ser feita no backend!');
    return { success: !!token, score: token ? 0.8 : 0 };
  }

  // Se tiver Secret Key (não recomendado para produção), valida com Google
  try {
    const response = await fetch(
      `https://www.google.com/recaptcha/api/siteverify?secret=${secretKey}&response=${token}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );

    const data: RecaptchaVerificationResponse = await response.json();

    if (!data.success) {
      const errors = data['error-codes'] || [];
      return {
        success: false,
        score: 0,
        error: `reCAPTCHA validation failed: ${errors.join(', ')}`,
      };
    }

    // Score de 0.0 a 1.0
    // 1.0 = muito provável que seja humano
    // 0.0 = muito provável que seja bot
    const score = data.score || 0;
    
    // Em desenvolvimento, usar threshold mais baixo (localhost pode ter scores menores)
    // Em produção, usar threshold mais alto para maior segurança
    const threshold = process.env.NODE_ENV === 'development' ? 0.3 : 0.5;
    
    const isSuccess = score >= threshold;
    
    // Log detalhado em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 reCAPTCHA Score: ${score.toFixed(2)}, Threshold: ${threshold}, Resultado: ${isSuccess ? '✅ Aprovado' : '❌ Bloqueado'}`);
    }

    return {
      success: isSuccess,
      score,
      error: !isSuccess ? `Score muito baixo (${score.toFixed(2)}). Possível bot detectado.` : undefined,
    };
  } catch (error: any) {
    console.error('Erro ao validar reCAPTCHA:', error);
    return {
      success: false,
      score: 0,
      error: error.message || 'Erro ao validar reCAPTCHA',
    };
  }
};

/**
 * Verifica se o reCAPTCHA está configurado
 */
export const isRecaptchaConfigured = (): boolean => {
  return !!(
    process.env.REACT_APP_RECAPTCHA_SITE_KEY &&
    process.env.REACT_APP_RECAPTCHA_SITE_KEY !== ''
  );
};

